import { addWallet, pushLedger } from './ledger';
import { CLOCK_SKEW_MS, CLOSED_CAP, STREAK_MARKS } from './tables';
import type { MatchOutcome, Profile, StreakState } from './types';

export function emptyStreak(): StreakState {
  return { eventId: null, endsAt: null, wins: 0, save: 'locked', claimed: [], closedIds: [] };
}

function mondayOf(now: number): Date {
  const date = new Date(now);
  const midnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = midnight.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  return new Date(midnight.getFullYear(), midnight.getMonth(), midnight.getDate() + offset);
}

function weekIdFromMonday(monday: Date): string {
  const thursday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 3);
  const year = thursday.getFullYear();
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay();
  const jan4Offset = jan4Day === 0 ? -6 : 1 - jan4Day;
  const week1 = new Date(year, 0, 4 + jan4Offset);
  const diff = Math.round((monday.getTime() - week1.getTime()) / (7 * 86_400_000));
  const week = diff + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * Monday 00:00 local through the next Monday. The id is that Monday's ISO week.
 * The week stays open the whole time, so a broken streak can start again the same day.
 */
export function streakWindow(now: number): { open: boolean; id: string; endsAt: number } {
  const monday = mondayOf(now);
  const next = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7);
  return {
    open: true,
    id: weekIdFromMonday(monday),
    endsAt: next.getTime(),
  };
}

/** Hub and events copy. A broken streak invites a new one; a live one shows the count. */
export function streakCardLine(wins: number): string {
  if (wins <= 0) return 'Start a new streak';
  if (wins === 1) return '1 win';
  return `${wins} wins`;
}

export function streakTimeLeft(now: number, endsAt: number): string {
  const hours = Math.max(0, Math.ceil((endsAt - now) / 3_600_000));
  if (hours >= 48) {
    const days = Math.ceil(hours / 24);
    return `${days} days left`;
  }
  if (hours > 24) return '1 day left';
  if (hours === 24) return '1 day left';
  if (hours === 1) return '1 hour left';
  if (hours === 0) return 'Closing';
  return `${hours} hours left`;
}

function closeEvent(profile: Profile): void {
  const id = profile.streak.eventId;
  if (id) {
    profile.streak.closedIds.push(id);
    if (profile.streak.closedIds.length > CLOSED_CAP) {
      profile.streak.closedIds.splice(0, profile.streak.closedIds.length - CLOSED_CAP);
    }
  }
  profile.streak.eventId = null;
  profile.streak.endsAt = null;
  profile.streak.wins = 0;
  profile.streak.save = 'locked';
  profile.streak.claimed = [];
}

export function syncEvent(profile: Profile, now: number): { clockBack: boolean } {
  if (now < profile.lastSeenAt - CLOCK_SKEW_MS) return { clockBack: true };
  if (profile.streak.eventId && profile.streak.endsAt !== null && now >= profile.streak.endsAt) {
    closeEvent(profile);
  }
  const window = streakWindow(now);
  if (!window.open) return { clockBack: false };
  if (profile.streak.closedIds.includes(window.id)) return { clockBack: false };
  if (profile.streak.eventId === window.id) return { clockBack: false };
  if (profile.streak.eventId) closeEvent(profile);
  profile.streak.eventId = window.id;
  profile.streak.endsAt = window.endsAt;
  profile.streak.wins = 0;
  profile.streak.save = 'locked';
  profile.streak.claimed = [];
  return { clockBack: false };
}

function eventLive(profile: Profile, now: number): boolean {
  return profile.streak.eventId !== null && profile.streak.endsAt !== null && now < profile.streak.endsAt;
}

export interface StreakGrant {
  acorns: number;
  honey: number;
  wins: number;
  save: StreakState['save'];
  /** True when this match claimed the five-win prize. */
  headline: boolean;
}

function claimMarks(profile: Profile, now: number): { acorns: number; honey: number; headline: boolean } {
  let acorns = 0;
  let honey = 0;
  let headline = false;
  for (const mark of STREAK_MARKS) {
    if (profile.streak.wins < mark.wins) continue;
    if (profile.streak.claimed.includes(mark.wins)) continue;
    profile.streak.claimed.push(mark.wins);
    if (mark.role === 'headline') headline = true;
    acorns += mark.acorns;
    honey += mark.honey;
    const currencies: { acorns?: number; honey?: number } = {};
    if (mark.acorns) currencies.acorns = mark.acorns;
    if (mark.honey) currencies.honey = mark.honey;
    pushLedger(profile, now, 'streak', currencies, 0, `streak:${mark.wins}`);
  }
  if (acorns || honey) addWallet(profile, { acorns, honey });
  return { acorns, honey, headline };
}

/**
 * Ranked result inside an open week. A tie holds. The save arms at 3 and spends once,
 * dropping the streak by 1 (minimum 1) instead of clearing it.
 */
export function applyStreak(profile: Profile, outcome: MatchOutcome, now: number): StreakGrant {
  const clock = syncEvent(profile, now);
  const idle: StreakGrant = {
    acorns: 0,
    honey: 0,
    wins: profile.streak.wins,
    save: profile.streak.save,
    headline: false,
  };
  if (clock.clockBack || !eventLive(profile, now)) return idle;
  if (outcome === 'tie') return idle;
  if (outcome === 'win') {
    profile.streak.wins += 1;
    if (profile.streak.wins >= 3 && profile.streak.save === 'locked') profile.streak.save = 'ready';
    const gained = claimMarks(profile, now);
    return { ...gained, wins: profile.streak.wins, save: profile.streak.save };
  }
  if (profile.streak.save === 'ready' && profile.streak.wins >= 3) {
    profile.streak.wins = Math.max(1, profile.streak.wins - 1);
    profile.streak.save = 'used';
  } else {
    profile.streak.wins = 0;
  }
  return { acorns: 0, honey: 0, wins: profile.streak.wins, save: profile.streak.save, headline: false };
}

export interface StreakExpect {
  acorns: number;
  honey: number;
  /** Reach rates for marks 1, 2, 3, 5, 7, 10. */
  reach: number[];
}

/** Expected prizes over `matches` win/loss trials, including the one save. */
export function expectedStreakValue(winRate: number, matches = 30): StreakExpect {
  const marks = STREAK_MARKS.map((mark) => mark.wins);
  const width = 11;
  const saves = 3;
  const masks = 64;
  const index = (wins: number, save: number, mask: number) => (wins * saves + save) * masks + mask;
  let prob = new Float64Array(width * saves * masks);
  prob[index(0, 0, 0)] = 1;
  let acorns = 0;
  let honey = 0;
  const loss = 1 - winRate;

  for (let n = 0; n < matches; n++) {
    const next = new Float64Array(prob.length);
    for (let wins = 0; wins < width; wins++) {
      for (let save = 0; save < saves; save++) {
        for (let mask = 0; mask < masks; mask++) {
          const p = prob[index(wins, save, mask)];
          if (p === 0) continue;
          const nextWins = Math.min(10, wins + 1);
          let nextSave = save;
          if (nextWins >= 3 && nextSave === 0) nextSave = 1;
          let nextMask = mask;
          let gainA = 0;
          let gainH = 0;
          for (let bit = 0; bit < marks.length; bit++) {
            if (nextWins >= marks[bit] && (nextMask & (1 << bit)) === 0) {
              nextMask |= 1 << bit;
              gainA += STREAK_MARKS[bit].acorns;
              gainH += STREAK_MARKS[bit].honey;
            }
          }
          acorns += p * winRate * gainA;
          honey += p * winRate * gainH;
          next[index(nextWins, nextSave, nextMask)] += p * winRate;

          let lostWins = 0;
          let lostSave = save;
          if (save === 1 && wins >= 3) {
            lostWins = Math.max(1, wins - 1);
            lostSave = 2;
          }
          next[index(lostWins, lostSave, mask)] += p * loss;
        }
      }
    }
    prob = next;
  }

  const reach = marks.map(() => 0);
  for (let wins = 0; wins < width; wins++) {
    for (let save = 0; save < saves; save++) {
      for (let mask = 0; mask < masks; mask++) {
        const p = prob[index(wins, save, mask)];
        if (p === 0) continue;
        for (let bit = 0; bit < marks.length; bit++) {
          if (mask & (1 << bit)) reach[bit] += p;
        }
      }
    }
  }
  return { acorns, honey, reach };
}
